import { useCallback } from "react";

import {
  useDelete,
  useGo,
  useInvalidate,
  useNotification,
} from "@refinedev/core";
import { Row } from "@tanstack/react-table";
import { Copy, Edit, MapPin, Trash2, Users } from "lucide-react";

import { ContextConfig } from "@/components/command-bar/use-command-bar-context";
import { IEventData } from "@/types/event";

export const useEventRowActions = (): ((
  row: Row<IEventData>,
) => ContextConfig) => {
  const go = useGo();
  const { mutateAsync } = useDelete();
  const invalidate = useInvalidate();
  const { open } = useNotification();

  const getRowActions = useCallback(
    (row: Row<IEventData>) => {
      return {
        badge: row.original.document.name,
        label: `Actions for ${row.original.document.name}`,
        groups: [
          {
            id: "row-main",
            heading: "Main Actions",
            actions: [
              {
                id: "copy-id",
                label: "Copy ID",
                description: "Copy Event Id",
                icon: <Copy className="h-4 w-4" />,
                onSelect: () => {
                  navigator.clipboard.writeText(row.original.document.id);
                  open?.({
                    message: "Event ID copied",
                    type: "success",
                  });
                },
              },
              {
                id: "edit",
                label: "Edit Event",
                description: "Modify event details",
                icon: <Edit className="h-4 w-4" />,
                onSelect: () =>
                  go({ to: `/events/edit/${row.original.document.id}` }),
              },
              {
                id: "clone",
                label: "Clone Event",
                description: "Create a duplicate",
                icon: <Copy className="h-4 w-4" />,
                onSelect: () =>
                  go({ to: `/events/clone/${row.original.document.id}` }),
              },
            ],
          },
          {
            id: "row-navigate",
            heading: "Navigate",
            actions: [
              {
                id: "venue",
                label: "View Venue",
                description: row.original.document.venue.name,
                icon: <MapPin className="h-4 w-4" />,
                onSelect: () =>
                  go({
                    to: `/venues/edit/${row.original.document.venue.id}`,
                  }),
              },
              {
                id: "attendees",
                label: "View Orders",
                description: `View event's orders`,
                icon: <Users className="h-4 w-4" />,
                onSelect: () =>
                  go({
                    to: `/orders?query=${row.original.document.name}&queryBy=metadata.event.name`,
                  }),
              },
            ],
          },
          {
            id: "row-danger",
            heading: "Danger Zone",
            actions: [
              {
                id: "delete",
                label: "Delete Event",
                description: "Permanently remove",
                icon: <Trash2 className="h-4 w-4" />,
                onSelect: async () => {
                  await mutateAsync({
                    resource: "events",
                    id: row.original.document.id,
                    mutationMode: "undoable",
                  });
                  setTimeout(
                    () =>
                      invalidate({
                        resource: "events",
                        invalidates: ["list"],
                      }),
                    2000,
                  );
                },
                destructive: true,
              },
            ],
          },
        ],
      };
    },
    [go],
  );

  return getRowActions;
};
